import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { ApiError } from '../api/client';
import { isTwoStep, useLogin, useLoginCode, useMe } from '../auth/session';
import { applyLanguage, LANGUAGES } from '../i18n';
import ui from '../styles/ui.module.css';
import styles from './LoginPage.module.css';
import { Brand } from '../components/Logo';
import { PasswordInput } from '../components/PasswordInput';

/** The login error to show, from the server's code. */
function errorText(error: unknown, t: (key: string) => string): string | null {
  if (!error) return null;
  const code = error instanceof ApiError ? error.code : undefined;
  switch (code) {
    case 'invalidCredentials':
      return t('login.invalidCredentials');
    case 'accountDeactivated':
      return t('login.deactivated');
    case 'adminSetupRequired':
      return t('login.adminSetupRequired');
    case 'invalidCode':
      return t('login.invalidCode');
    case 'codeExpired':
      return t('login.codeExpired');
    default:
      return t('login.failed');
  }
}

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const login = useLogin();
  const loginCode = useLoginCode();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // The administrator's second step: the 6-digit code from the authenticator app.
  const [codeStep, setCodeStep] = useState(false);
  const [code, setCode] = useState('');

  const from = (location.state as { from?: string } | null)?.from ?? '/';
  if (me.data) return <Navigate to={me.data.admin ? '/admin/users' : from} replace />;

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    loginCode.reset();
    login.mutate(
      { email, password },
      {
        onSuccess: (result) => {
          if (isTwoStep(result)) {
            setCodeStep(true);
            setPassword('');
          } else {
            navigate(from, { replace: true });
          }
        },
      },
    );
  }

  function onCode(event: FormEvent) {
    event.preventDefault();
    loginCode.mutate(code, {
      onSuccess: () => navigate('/admin/users', { replace: true }),
      onError: (error) => {
        // Too late or too many tries: back to the password.
        if (error instanceof ApiError && error.code === 'codeExpired') {
          setCodeStep(false);
          setCode('');
        }
      },
    });
  }

  // Back at the password after an expired code, that is the message to show.
  const error = codeStep ? errorText(loginCode.error, t) : errorText(login.error ?? loginCode.error, t);

  return (
    <div className={styles.wrap}>
      {codeStep ? (
        <form className={`${ui.card} ${styles.card}`} onSubmit={onCode} noValidate>
          <Brand size={52} className={styles.brand} />
          <h1 className={styles.title}>{t('login.codeTitle')}</h1>
          <p className={ui.muted}>{t('login.codeHint')}</p>
          {error && (
            <div className={`${ui.notice} ${ui.noticeError}`} role="alert">
              {error}
            </div>
          )}
          <label className={ui.field}>
            <span className={ui.label}>{t('login.code')}</span>
            <input
              className={`${ui.input} ${styles.code}`}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
          </label>
          <button type="submit" className={`${ui.button} ${ui.primary} ${styles.submit}`} disabled={code.length !== 6 || loginCode.isPending}>
            {t('login.verify')}
          </button>
        </form>
      ) : (
        <form className={`${ui.card} ${styles.card}`} onSubmit={onSubmit} noValidate>
          <Brand size={52} className={styles.brand} />
          <p className={ui.muted}>{t('app.tagline')}</p>
          <h1 className={styles.title}>{t('login.title')}</h1>
          {error && (
            <div className={`${ui.notice} ${ui.noticeError}`} role="alert">
              {error}
            </div>
          )}
          <label className={ui.field}>
            <span className={ui.label}>{t('login.email')}</span>
            <input
              className={ui.input}
              type="email"
              name="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <div className={ui.field}>
            <label htmlFor="login-password" className={ui.label}>
              {t('login.password')}
            </label>
            <PasswordInput
              id="login-password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <button
            type="submit"
            className={`${ui.button} ${ui.primary} ${styles.submit}`}
            disabled={login.isPending || !email || !password}
          >
            {login.isPending ? t('login.submitting') : t('login.submit')}
          </button>
          <div className={styles.languages} role="group" aria-label={t('profile.language')}>
            {LANGUAGES.map((language) => (
              <button
                key={language}
                type="button"
                lang={language}
                aria-pressed={i18n.language === language}
                className={styles.languageButton}
                onClick={() => applyLanguage(language)}
              >
                {t(`languages.${language}`)}
              </button>
            ))}
          </div>
        </form>
      )}
    </div>
  );
}
